import { Component, Input, OnInit, Optional, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OwlOptions } from 'ngx-owl-carousel-o';
import ONBOARDING_FLOW_DATA from '../../../../../assets/data/onboarding.json'
import LANDING_FEATURES_DATA from '../../../../../assets/data/landing-page-features.json'
import { CommonService } from 'src/app/services/Common-function/common.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { ShakeAnimationService } from 'src/app/services/shake-animation/shake-animation.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';

type VideoLanguage = 'hn' | 'en';

@Component({
  selector: 'ipm-onboarding-flow',
  templateUrl: './onboarding-flow.component.html',
  styleUrls: ['./onboarding-flow.component.scss'],
  // No animations needed with Owl Carousel
})
export class OnboardingFlowComponent implements OnInit {
  @Input() isPopUp: boolean = true;
  @Input() userRole: string = '';
  @Input() isBecomingProducer: boolean = false;
  @Input() isLoggedIn : boolean = false;
  @ViewChild('mobileTabs') mobileTabs: ElementRef;
  onboardingFlowData : any = ONBOARDING_FLOW_DATA;
  landingFeaturesData : any = LANDING_FEATURES_DATA;
  currrentType:'seller'|'buyer' = 'buyer';
  filteredOnboardingFlowData: any[] = [];
  errorMessage: string = "";
  // Feature section variables for landing page
  activeButton: number = 0;
  previousButton: number = 0;
  slideDirection: string = '';
  animatingFeature: boolean = false;
  
  // Video toggles
  selectedVideoLanguage: VideoLanguage = 'hn';
  activeVideoUrl: SafeResourceUrl | null = null;
  
  // Touch and Mouse gesture variables
  private startX: number = 0;
  private endX: number = 0;
  private isDragging: boolean = false;
  private minSwipeDistance: number = 50;
  
  customOptions: OwlOptions = {
    loop: false,
    dots: true,
    nav: false,
    items: 1,
    margin: 0,
    autoHeight: false,
    responsive: {
      0: { items: 1 },
      600: { items: 1 },
      1024: { items: 1 },
      1366: { items: 1 },
      1920: { items: 1 },
      2560: { items: 1 },
      3840: { items: 1 }
    }
  };
  mobileOptions: OwlOptions = {
    ...this.customOptions,
    autoHeight: true,
  };

  currentSlide: number = 0;

  get isLastSlide(): boolean {
    return this.currentSlide === this.filteredOnboardingFlowData.length - 1;
  }

  onSlideChanged(event: any): void {
    if (event && event.startPosition !== undefined) {
      this.currentSlide = event.startPosition;
    }
  }

  goToPrevSlide(carousel: any): void {
    carousel.prev();
  }

  goToNextSlide(carousel: any): void {
    carousel.next();
  }

  continueToDashboard(): void {
    if(this.isBecomingProducer){
      this.registerProducer();
      const targetPath = '/dashboard/keyPerformanceIndicators'
      // Use full page navigation so the window actually reloads after onboarding.
      setTimeout(() => {
        window.location.assign(targetPath);
      }, 1000);
    }
    else{
      this.closeByDeviceType();
      this.router.navigate([ this.userRole === 'producer' ? '/dashboard/keyPerformanceIndicators' : 'listening-dashboard']);
    }
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<OnboardingFlowComponent>,
    @Optional() public bottomSheetRef: MatBottomSheetRef<OnboardingFlowComponent>,
    private router: Router,
    private sanitizer: DomSanitizer,
    public commonFunction: CommonService,
    private authService: AuthService,
    public shakeAnimation: ShakeAnimationService,
    private browserStorage: BrowserStorageService
  ) {
    this.activeVideoUrl = this.getSafeYouTubeUrl('2TpuNnLw5UQ');
  }

  ngOnInit(): void {  
    this.currrentType = this.userRole === 'producer' ? 'seller' : 'buyer';

    this.filteredOnboardingFlowData = this.onboardingFlowData.filter((item: any) => item.type === this.currrentType);
  }
  
  setVideoLanguage(language: VideoLanguage): void {
    this.selectedVideoLanguage = language;
    this.activeVideoUrl = this.getSafeYouTubeUrl(language === 'en' ? 'boH0oB_416M' : '2TpuNnLw5UQ');
  }

  private getSafeYouTubeUrl(videoId: string): SafeResourceUrl {
    const origin = window.location.origin;
    const url = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&autoplay=0&origin=${origin}&playsinline=1&controls=0&fs=0&iv_load_policy=3&disablekb=1&vq=hd720`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // Optionally keep this if used elsewhere
  makeOnboardingDataArray(): any[] {
    return this.onboardingFlowData.filter((item: any) => 
      item.type === this.currrentType
    );
  }

  handleBtnClick(buttonIndex: number) {
    if (this.animatingFeature) {
      return; // Prevent clicks during animation
    }
    
    const previousIndex = this.activeButton;
    if (previousIndex === buttonIndex) {
      return; // Same button, no animation needed
    }
    
    const direction = buttonIndex > previousIndex ? 'left' : 'right';
    
    this.previousButton = previousIndex;
    this.slideDirection = direction;
    this.animatingFeature = true;
    this.activeButton = buttonIndex;
    setTimeout(()=>{
      this.scrollActiveTabIntoView();
    },0); // Trigger change detection to update the view before starting animation 
    // After animation completes (600ms), reset animation state
    setTimeout(() => {
      this.animatingFeature = false;
      this.slideDirection = '';
    }, 600);
  }

  // Touch event handlers
  onTouchStart(event: TouchEvent): void {
    this.startX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent): void {
    this.endX = event.changedTouches[0].clientX;
    this.handleSwipeGesture();
  }

  // Mouse event handlers
  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startX = event.clientX;
    event.preventDefault(); // Prevent text selection during drag
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    // Optional: You can add visual feedback here during drag
  }

  onMouseUp(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.endX = event.clientX;
    this.handleSwipeGesture();
  }

  onMouseLeave(): void {
    // Reset if mouse leaves the container while dragging
    this.isDragging = false;
  }

  // Common handler for both touch and mouse gestures
  handleSwipeGesture(): void {
    const swipeDistance = this.endX - this.startX;
    
    // Swipe left (next feature)
    if (swipeDistance < -this.minSwipeDistance) {
      const nextIndex = this.activeButton + 1;
      if (nextIndex < this.landingFeaturesData.length) {
        this.handleBtnClick(nextIndex);
      }
    }
    
    // Swipe right (previous feature)
    else if (swipeDistance > this.minSwipeDistance) {
      const prevIndex = this.activeButton - 1;
      if (prevIndex >= 0) {
        this.handleBtnClick(prevIndex);
      }
    }
  }

  closeByDeviceType(type: string = ""): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    } else {
      this.commonFunction.closePopUp();
    }
    if (type === 'reload') {
      const targetPath = this.currrentType === 'seller'
        ? '/dashboard/keyPerformanceIndicators'
        : '/listening-dashboard';
      // Use full page navigation so the window actually reloads after onboarding.
      window.location.assign(targetPath);
      return;
    }
  }
    /* Register Join Function */
  registerProducer() {
    this.errorMessage = "";
    const DATA = {
      "name": "",
      "email": "",
      "work_link": "",
      "social_media_link": "",
      "personal_notes": ""
    }
      this.authService.isAcceptTermConditionSeller().subscribe( res => {
        this.authService.addInfoProducer(DATA).subscribe( res => {
          this.closeByDeviceType('reload');
        }, (error) => {
          this.errorMessage =  error.error.message;
          let className = this.commonFunction.getClassName('onboarding-flow-model');
          this.shakeAnimation.addShakeAnimation(className);
        });
      }, (error) => {
          this.errorMessage =  error.error.message;
          let className = this.commonFunction.getClassName('onboarding-flow-model');
          this.shakeAnimation.addShakeAnimation(className);
      });

  }

  scrollActiveTabIntoView() {
    if(this.mobileTabs && this.mobileTabs.nativeElement){
      const tabContainer = this.mobileTabs.nativeElement;
      const activeTab = tabContainer.children[this.activeButton];
      if(activeTab){
        activeTab.scrollIntoView({ 
          behavior: 'smooth', 
          inline: 'center', 
          block: 'nearest' 
        });
      }
    }
  }

  handleCtaClick() {
    if(this.isLoggedIn){
      this.continueToDashboard();
    } else {
      this.router.navigate(['/beats']);
    } 
  }

}
